// The shape of the whole Bible — every book, every chapter, every verse slot.
//
// This is what lets a player's Bible render completely offline. The words are
// fetched a chapter at a time (lib/bible.ts → bible-api), but the *structure* is
// local, so the app can draw all 31,102 verse slots and shade each one by what
// the player has done with it without a single network request. A verse the
// player has never opened still has a place on the page waiting for them.
//
// Versification is the traditional Protestant (KJV/WEB) numbering, which is what
// bible-api serves. A handful of translations differ by a verse or two in a few
// chapters (3 John and the ends of some Psalms are the usual culprits), so
// `noteRealVerseCount` lets a fetched chapter correct this table at runtime —
// the counts below are the starting picture, not an assertion the reader has to
// defend. scripts/check-structure.mjs verifies every book's chapter count and
// verse total against published figures, so a typo here fails the build.

import { BIBLE_BOOKS, type Testament } from './pool'

// Verses per chapter, one comma-separated row per book, Genesis → Revelation.
// Stored as strings rather than number literals purely for legibility — a book
// is one line you can eyeball against a printed table.
const VERSES_PER_CHAPTER: Record<string, string> = {
  'Genesis': '31,25,24,26,32,22,24,22,29,32,32,20,18,24,21,16,27,33,38,18,34,24,20,67,34,35,46,22,35,43,55,32,20,31,29,43,36,30,23,23,57,38,34,34,28,34,31,22,33,26',
  'Exodus': '22,25,22,31,23,30,25,32,35,29,10,51,22,31,27,36,16,27,25,26,36,31,33,18,40,37,21,43,46,38,18,35,23,35,35,38,29,31,43,38',
  'Leviticus': '17,16,17,35,19,30,38,36,24,20,47,8,59,57,33,34,16,30,37,27,24,33,44,23,55,46,34',
  'Numbers': '54,34,51,49,31,27,89,26,23,36,35,16,33,45,41,50,13,32,22,29,35,41,30,25,18,65,23,31,40,16,54,42,56,29,34,13',
  'Deuteronomy': '46,37,29,49,33,25,26,20,29,22,32,32,18,29,23,22,20,22,21,20,23,30,25,22,19,19,26,68,29,20,30,52,29,12',
  'Joshua': '18,24,17,24,15,27,26,35,27,43,23,24,33,15,63,10,18,28,51,9,45,34,16,33',
  'Judges': '36,23,31,24,31,40,25,35,57,18,40,15,25,20,20,31,13,31,30,48,25',
  'Ruth': '22,23,18,22',
  '1 Samuel': '28,36,21,22,12,21,17,22,27,27,15,25,23,52,35,23,58,30,24,42,15,23,29,22,44,25,12,25,11,31,13',
  '2 Samuel': '27,32,39,12,25,23,29,18,13,19,27,31,39,33,37,23,29,33,43,26,22,51,39,25',
  '1 Kings': '53,46,28,34,18,38,51,66,28,29,43,33,34,31,34,34,24,46,21,43,29,53',
  '2 Kings': '18,25,27,44,27,33,20,29,37,36,21,21,25,29,38,20,41,37,37,21,26,20,37,20,30',
  '1 Chronicles': '54,55,24,43,26,81,40,40,44,14,47,40,14,17,29,43,27,17,19,8,30,19,32,31,31,32,34,21,30',
  '2 Chronicles': '17,18,17,22,14,42,22,18,31,19,23,16,22,15,19,14,19,34,11,37,20,12,21,27,28,23,9,27,36,27,21,33,25,33,27,23',
  'Ezra': '11,70,13,24,17,22,28,36,15,44',
  'Nehemiah': '11,20,32,23,19,19,73,18,38,39,36,47,31',
  'Esther': '22,23,15,17,14,14,10,17,32,3',
  'Job': '22,13,26,21,27,30,21,22,35,22,20,25,28,22,35,22,16,21,29,29,34,30,17,25,6,14,23,28,25,31,40,22,33,37,16,33,24,41,30,24,34,17',
  'Psalms': '6,12,8,8,12,10,17,9,20,18,7,8,6,7,5,11,15,50,14,9,13,31,6,10,22,12,14,9,11,12,24,11,22,22,28,12,40,22,13,17,13,11,5,26,17,11,9,14,20,23,19,9,6,7,23,13,11,11,17,12,8,12,11,10,13,20,7,35,36,5,24,20,28,23,10,12,20,72,13,19,16,8,18,12,13,17,7,18,52,17,16,15,5,23,11,13,12,9,9,5,8,28,22,35,45,48,43,13,31,7,10,10,9,8,18,19,2,29,176,7,8,9,4,8,5,6,5,6,8,8,3,18,3,3,21,26,9,8,24,13,10,7,12,15,21,10,20,14,9,6',
  'Proverbs': '33,22,35,27,23,35,27,36,18,32,31,28,25,35,33,33,28,24,29,30,31,29,35,34,28,28,27,28,27,33,31',
  'Ecclesiastes': '18,26,22,16,20,12,29,17,18,20,10,14',
  'Song of Solomon': '17,17,11,16,16,13,13,14',
  'Isaiah': '31,22,26,6,30,13,25,22,21,34,16,6,22,32,9,14,14,7,25,6,17,25,18,23,12,21,13,29,24,33,9,20,24,17,10,22,38,22,8,31,29,25,28,28,25,13,15,22,26,11,23,15,12,17,13,12,21,14,21,22,11,12,19,12,25,24',
  'Jeremiah': '19,37,25,31,31,30,34,22,26,25,23,17,27,22,21,21,27,23,15,18,14,30,40,10,38,24,22,17,32,24,40,44,26,22,19,32,21,28,18,16,18,22,13,30,5,28,7,47,39,46,64,34',
  'Lamentations': '22,22,66,22,22',
  'Ezekiel': '28,10,27,17,17,14,27,18,11,22,25,28,23,23,8,63,24,32,14,49,32,31,49,27,17,21,36,26,21,26,18,32,33,31,15,38,28,23,29,49,26,20,27,31,25,24,23,35',
  'Daniel': '21,49,30,37,31,28,28,27,27,21,45,13',
  'Hosea': '11,23,5,19,15,11,16,14,17,15,12,14,16,9',
  'Joel': '20,32,21',
  'Amos': '15,16,15,13,27,14,17,14,15',
  'Obadiah': '21',
  'Jonah': '17,10,10,11',
  'Micah': '16,13,12,13,15,16,20',
  'Nahum': '15,13,19',
  'Habakkuk': '17,20,19',
  'Zephaniah': '18,15,20',
  'Haggai': '15,23',
  'Zechariah': '21,13,10,14,11,15,14,23,17,12,17,14,9,21',
  'Malachi': '14,17,18,6',
  'Matthew': '25,23,17,25,48,34,29,34,38,42,30,50,58,36,39,28,27,35,30,34,46,46,39,51,46,75,66,20',
  'Mark': '45,28,35,41,43,56,37,38,50,52,33,44,37,72,47,20',
  'Luke': '80,52,38,44,39,49,50,56,62,42,54,59,35,35,32,31,37,43,48,47,38,71,56,53',
  'John': '51,25,36,54,47,71,53,59,41,42,57,50,38,31,27,33,26,40,42,31,25',
  'Acts': '26,47,26,37,42,15,60,40,43,48,30,25,52,28,41,40,34,28,41,38,40,30,35,27,27,32,44,31',
  'Romans': '32,29,31,25,21,23,25,39,33,21,36,21,14,23,33,27',
  '1 Corinthians': '31,16,23,21,13,20,40,13,27,33,34,31,13,40,58,24',
  '2 Corinthians': '24,17,18,18,21,18,16,24,15,18,33,21,14',
  'Galatians': '24,21,29,31,26,18',
  'Ephesians': '23,22,21,32,33,24',
  'Philippians': '30,30,21,23',
  'Colossians': '29,23,25,18',
  '1 Thessalonians': '10,20,13,18,28',
  '2 Thessalonians': '12,17,18',
  '1 Timothy': '20,15,16,16,25,21',
  '2 Timothy': '18,26,17,22',
  'Titus': '16,15,15',
  'Philemon': '25',
  'Hebrews': '14,18,19,16,14,20,28,13,28,39,40,29,25',
  'James': '27,26,18,17,20',
  '1 Peter': '25,25,22,19,14',
  '2 Peter': '21,22,18',
  '1 John': '10,29,24,21,21',
  '2 John': '13',
  '3 John': '14',
  'Jude': '25',
  'Revelation': '20,29,22,11,14,17,17,13,21,11,19,17,18,20,8,21,18,24,21,15,27,21',
}

// Parsed once. Every lookup below reads this, so the string rows above cost one
// split at module load and nothing afterwards.
const COUNTS: Record<string, number[]> = Object.fromEntries(
  Object.entries(VERSES_PER_CHAPTER).map(([book, row]) => [book, row.split(',').map(Number)]),
)

// Matthew opens the New Testament; everything before it is Old.
const NT_START = BIBLE_BOOKS.indexOf('Matthew')

export interface BookShape {
  book: string
  testament: Testament
  /** Verses in chapter n, at index n-1. */
  chapters: number[]
  verseTotal: number
}

export const BIBLE_SHAPE: BookShape[] = BIBLE_BOOKS.map((book, i) => {
  const chapters = COUNTS[book] ?? []
  return {
    book,
    testament: i >= NT_START ? 'NT' : 'OT',
    chapters,
    verseTotal: chapters.reduce((s, n) => s + n, 0),
  }
})

const SHAPE_BY_BOOK: Record<string, BookShape> = Object.fromEntries(
  BIBLE_SHAPE.map((b) => [b.book, b]),
)

export function shapeOf(book: string): BookShape | undefined {
  return SHAPE_BY_BOOK[book]
}

export function chapterCount(book: string): number {
  return SHAPE_BY_BOOK[book]?.chapters.length ?? 0
}

/** Verses in one chapter — 0 for a book or chapter that doesn't exist. */
export function verseCount(book: string, chapter: number): number {
  const shape = SHAPE_BY_BOOK[book]
  if (!shape || chapter < 1 || chapter > shape.chapters.length) return 0
  return shape.chapters[chapter - 1]
}

export function bookVerseTotal(book: string): number {
  return SHAPE_BY_BOOK[book]?.verseTotal ?? 0
}

/** Every verse in the Bible — the denominator for "how much have I opened?" */
export const TOTAL_VERSES: number = BIBLE_SHAPE.reduce((s, b) => s + b.verseTotal, 0)
export const TOTAL_CHAPTERS: number = BIBLE_SHAPE.reduce((s, b) => s + b.chapters.length, 0)

/** `Genesis|1` — the key a read chapter is stored under. */
export function chapterKey(book: string, chapter: number): string {
  return `${book}|${chapter}`
}

export function parseChapterKey(key: string): { book: string; chapter: number } | null {
  const at = key.lastIndexOf('|')
  if (at <= 0) return null
  const chapter = Number(key.slice(at + 1))
  if (!Number.isInteger(chapter) || chapter < 1) return null
  return { book: key.slice(0, at), chapter }
}

// A book's name and the name a citation uses aren't always the same word: the
// book is Psalms, but a verse in it is cited "Psalm 23:1" — which is exactly how
// the verse pool writes those 24 references. References have to match the pool
// character for character, because favorites are keyed by reference and a verse
// kept from a recap has to be the same verse when the Bible highlights it.
const CITATION_NAME: Record<string, string> = { Psalms: 'Psalm' }

/** The book name as a citation writes it — `Psalms` -> `Psalm`. */
export function citationBook(book: string): string {
  return CITATION_NAME[book] ?? book
}

// …and back the other way, plus the naming variants a stored reference might
// carry from elsewhere, so an old key still finds its place on the shelf.
const CANON_NAME: Record<string, string> = {
  'Psalm': 'Psalms',
  'Song of Songs': 'Song of Solomon',
  'Canticles': 'Song of Solomon',
  'Revelations': 'Revelation',
}

/** The book name as the shelf lists it — `Psalm` -> `Psalms`. */
export function canonBook(name: string): string {
  return CANON_NAME[name] ?? name
}

/** `John 3:16` / `Psalm 23:1` — matches the verse pool's reference format. */
export function verseReference(book: string, chapter: number, verse: number): string {
  return `${citationBook(book)} ${chapter}:${verse}`
}

// Translations disagree about a few chapter endings, so when a chapter is
// actually fetched we take the real count over the table above. It only ever
// grows the picture in memory for this session — the table stays the shipped
// default, and a short fetch (a truncated response) can't shrink a chapter and
// make already-read verses vanish.
const realCounts = new Map<string, number>()

export function noteRealVerseCount(book: string, chapter: number, verses: number) {
  if (!Number.isInteger(verses) || verses <= 0) return
  const key = chapterKey(book, chapter)
  const known = realCounts.get(key) ?? verseCount(book, chapter)
  if (verses > known) realCounts.set(key, verses)
}

/** Verses in a chapter, preferring what the API actually returned this session. */
export function effectiveVerseCount(book: string, chapter: number): number {
  return realCounts.get(chapterKey(book, chapter)) ?? verseCount(book, chapter)
}
